import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FilterMatchMode } from 'primereact/api';
import { selectNotificationsData, selectLoading, fetchNotifications } from '../store/slices/notificationsSlice';
import NotificationsDialog from './NotificationsDialog';
import { isSuperAdmin, getOwnClientId } from './notificationScope';
import { NOTIFICATION_STATUSES, normalizeStatus, statusTone, countByStatus } from './notificationStatus';
import { saveNotification, getClientsList, getBranches, getNotificationReadStatus } from '../utilities/api';
import './Notifications.css';

/** 'YYYY-MM-DD HH:mm:ss' -> 'dd/mm/yyyy hh:mm'. Blank stays blank. */
const formatDisplayDate = (value) => {
    if (!value) return '';
    const date = new Date(String(value).replace(' ', 'T'));
    if (isNaN(date.getTime())) return String(value);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const isExpired = (value) => {
    if (!value) return false;
    const date = new Date(String(value).replace(' ', 'T'));
    return !isNaN(date.getTime()) && date.getTime() < Date.now();
};

/** Rounded status chip, matching the pills on the endpoint pages. */
const Pill = ({ tone, children }) => <span className={`nf-pill nf-pill-${tone}`}>{children}</span>;

const Dash = () => <span className="nf-muted-cell">—</span>;

const EMPTY_READ_STATUS = { read: [], unread: [], read_count: 0, recipient_count: 0 };

const NotificationsContent = () => {
    const dispatch = useDispatch();
    const toast = useRef(null);

    const notificationsData = useSelector(selectNotificationsData);
    const loading = useSelector(selectLoading);

    const superAdmin = isSuperAdmin();
    const ownClientId = getOwnClientId();

    const [statusFilter, setStatusFilter] = useState('Active');
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
    const [displayDialog, setDisplayDialog] = useState(false);
    const [editNotification, setEditNotification] = useState(null);

    // Client/branch filters. A non-GSPOS user is pinned to its own client and cannot
    // widen the filter — the server enforces the same thing.
    const [clientFilter, setClientFilter] = useState(superAdmin ? null : ownClientId || null);
    const [branchFilter, setBranchFilter] = useState(null);
    const [clientOptions, setClientOptions] = useState([]);
    const [branchOptions, setBranchOptions] = useState([]);

    const [selectedNotification, setSelectedNotification] = useState(null);
    const [readStatus, setReadStatus] = useState(EMPTY_READ_STATUS);
    const [readStatusLoading, setReadStatusLoading] = useState(false);

    const reload = useCallback(() => {
        const params = {};
        // Fall back to '0' so a non-GSPOS user with no client on record matches nothing
        // rather than everything.
        if (superAdmin) {
            if (clientFilter) params.client_id = clientFilter;
        } else {
            params.client_id = ownClientId || '0';
        }
        if (branchFilter) params.branch_id = branchFilter;
        dispatch(fetchNotifications(params));
    }, [dispatch, superAdmin, ownClientId, clientFilter, branchFilter]);

    useEffect(() => {
        reload();
    }, [reload]);

    useEffect(() => {
        let cancelled = false;
        getClientsList()
            .then((rows) => {
                if (cancelled) return;
                setClientOptions(
                    rows
                        .filter((c) => superAdmin || String(c.client_id) === ownClientId)
                        .map((c) => ({ label: c.client_name, value: String(c.client_id) }))
                );
            })
            .catch(() => !cancelled && setClientOptions([]));
        return () => {
            cancelled = true;
        };
    }, [superAdmin, ownClientId]);

    // Branch options follow the selected client.
    useEffect(() => {
        if (!clientFilter) {
            setBranchOptions([]);
            return undefined;
        }
        let cancelled = false;
        getBranches(clientFilter)
            .then((rows) => {
                if (cancelled) return;
                setBranchOptions(rows.map((b) => ({ label: b.branch_name, value: String(b.branch_id) })));
            })
            .catch(() => !cancelled && setBranchOptions([]));
        return () => {
            cancelled = true;
        };
    }, [clientFilter]);

    const loadReadStatus = useCallback(async (notification) => {
        if (!notification) {
            setReadStatus(EMPTY_READ_STATUS);
            return;
        }
        setReadStatusLoading(true);
        try {
            setReadStatus(await getNotificationReadStatus(notification.notification_id));
        } catch {
            setReadStatus(EMPTY_READ_STATUS);
        } finally {
            setReadStatusLoading(false);
        }
    }, []);

    const handleSelectionChange = (notification) => {
        setSelectedNotification(notification);
        loadReadStatus(notification);
    };

    const statusCounts = useMemo(
        () => Object.fromEntries(NOTIFICATION_STATUSES.map((s) => [s.value, countByStatus(notificationsData, s.value)])),
        [notificationsData]
    );

    const filteredData = useMemo(() => notificationsData.filter((item) => normalizeStatus(item.status) === statusFilter), [notificationsData, statusFilter]);

    /** Read receipts and unread recipients merged into one list, read first. */
    const readStatusRows = useMemo(
        () => [
            ...(readStatus.read || []).map((r) => ({
                user_id: r.user_id,
                username: r.username,
                role: r.role,
                branch_name: r.branch_name,
                read_at: r.read_at,
                remarks: r.remarks,
                is_read: true
            })),
            ...(readStatus.unread || []).map((u) => ({
                user_id: u.userId,
                username: u.username,
                role: u.role,
                branch_name: u.branch_name,
                read_at: null,
                remarks: null,
                is_read: false
            }))
        ],
        [readStatus]
    );

    const handleSaveRequest = (notification) => {
        const isEdit = !!notification.notification_id;
        confirmDialog({
            message: isEdit ? 'Are you sure you want to update this notification?' : 'Are you sure you want to add this new notification?',
            header: isEdit ? 'Confirm Update' : 'Confirm Add',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, Save',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-success',
            rejectClassName: 'p-button-text',
            accept: () => handleConfirmedSave(notification)
        });
    };

    const handleConfirmedSave = async (notification) => {
        const isEdit = !!notification.notification_id;
        try {
            const data = await saveNotification(notification);
            // The API answers 200 with status:false for "nothing changed" — don't call that a success.
            if (data?.status === false) {
                toast.current.show({ severity: 'warn', summary: 'Not Saved', detail: data?.message || 'Nothing changed', life: 4000 });
                return;
            }
            const msg = data?.message || (isEdit ? 'Notification updated successfully' : 'Notification added successfully');
            toast.current.show({ severity: 'success', summary: 'Success', detail: msg, life: 3000 });
            setDisplayDialog(false);
            setEditNotification(null);
            reload();
            if (isEdit && selectedNotification?.notification_id === notification.notification_id) {
                loadReadStatus(selectedNotification);
            }
        } catch (err) {
            const detail = err?.response?.data?.message || err?.message || 'Save failed. Please try again.';
            toast.current.show({ severity: 'error', summary: 'Error', detail, life: 4000 });
        }
    };

    const detailsBodyTemplate = (rowData) => (
        <span title={rowData.notification_details} style={{ display: 'block', maxWidth: '340px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {rowData.notification_details}
        </span>
    );

    const branchBodyTemplate = (rowData) => (rowData.branch_id ? rowData.branch_name || rowData.branch_id : <Pill tone="secondary">All Branches</Pill>);

    const expiryBodyTemplate = (rowData) => {
        if (!rowData.expiry_date) return <span className="nf-muted-cell">Never</span>;
        return isExpired(rowData.expiry_date) ? (
            <span className="nf-expired">{formatDisplayDate(rowData.expiry_date)} (expired)</span>
        ) : (
            formatDisplayDate(rowData.expiry_date)
        );
    };

    const readBodyTemplate = (rowData) => {
        const read = Number(rowData.read_count) || 0;
        const total = Number(rowData.recipient_count) || 0;
        const tone = total > 0 && read >= total ? 'success' : read > 0 ? 'info' : 'warning';
        return (
            <Pill tone={tone}>
                {read} / {total}
            </Pill>
        );
    };

    const statusBodyTemplate = (rowData) => <Pill tone={statusTone(rowData.status)}>{normalizeStatus(rowData.status)}</Pill>;

    // Authoring is GSPOS-only, and the server enforces the same rule — this just keeps
    // non-GSPOS users from being offered a button that would come back 403.
    const actionBodyTemplate = (rowData) => (
        <Button
            icon="pi pi-pencil"
            rounded
            text
            className="p-button-warning"
            onClick={(e) => {
                e.stopPropagation();
                setEditNotification(rowData);
                setDisplayDialog(true);
            }}
            tooltip="Edit"
            tooltipOptions={{ position: 'top' }}
        />
    );

    const remarksBodyTemplate = (rowData) =>
        rowData.remarks ? (
            <span title={rowData.remarks} style={{ display: 'block', maxWidth: '260px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {rowData.remarks}
            </span>
        ) : (
            <Dash />
        );

    const unreadCount = Math.max(0, (readStatus.recipient_count || 0) - (readStatus.read_count || 0));

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* ── Filters ─────────────────────────────────────────── */}
            <div className="nf-card nf-toolbar">
                <div className="nf-field">
                    <label className="nf-field-label">
                        <i className="pi pi-briefcase" /> Client {!superAdmin && <span className="nf-muted">(locked to yours)</span>}
                    </label>
                    <Dropdown
                        value={clientFilter}
                        options={clientOptions}
                        onChange={(e) => {
                            setClientFilter(e.value);
                            // A branch from the previous client would filter everything out.
                            setBranchFilter(null);
                            handleSelectionChange(null);
                        }}
                        placeholder={superAdmin ? 'All Clients' : 'Your Client'}
                        filter={superAdmin}
                        showClear={superAdmin}
                        disabled={!superAdmin}
                        className="w-full"
                    />
                </div>

                <div className="nf-field">
                    <label className="nf-field-label">
                        <i className="pi pi-building" /> Branch
                    </label>
                    <Dropdown
                        value={branchFilter}
                        options={branchOptions}
                        onChange={(e) => {
                            setBranchFilter(e.value);
                            handleSelectionChange(null);
                        }}
                        placeholder={clientFilter ? 'All Branches' : 'Select a client first'}
                        filter
                        showClear
                        disabled={!clientFilter}
                        className="w-full"
                    />
                </div>

                <div className="nf-field">
                    <label className="nf-field-label">
                        <i className="pi pi-search" /> Search
                    </label>
                    <InputText
                        value={globalFilterValue}
                        onChange={(e) => {
                            setGlobalFilterValue(e.target.value);
                            setFilters({ global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS } });
                        }}
                        placeholder="Notification, client, branch, author…"
                        className="w-full"
                    />
                </div>

                {superAdmin && (clientFilter || branchFilter) && (
                    <Button label="Clear" icon="pi pi-filter-slash" className="nf-clear" onClick={() => { setClientFilter(null); setBranchFilter(null); handleSelectionChange(null); }} />
                )}

                {superAdmin && (
                    <Button
                        label="Add Notification"
                        icon="pi pi-plus"
                        className="nf-add"
                        onClick={() => {
                            setEditNotification(null);
                            setDisplayDialog(true);
                        }}
                    />
                )}

                {branchFilter && (
                    <span className="nf-note">
                        <i className="pi pi-info-circle" /> Branch filter also includes alerts sent to all branches
                    </span>
                )}
            </div>

            {/* ── List ────────────────────────────────────────────── */}
            <div className="nf-card">
                <div className="nf-card-head">
                    <div>
                        <h3 className="nf-card-title">
                            <i className="pi pi-list" /> Notification List
                        </h3>
                        <p className="nf-card-sub">Select a row to see who has read it</p>
                    </div>
                    <div className="nf-tabs">
                        {NOTIFICATION_STATUSES.map((s) => (
                            <button key={s.value} className={`nf-tab${statusFilter === s.value ? ' selected' : ''}`} onClick={() => setStatusFilter(s.value)}>
                                <i className={s.icon} />
                                {s.label} <span className="nf-tab-count">{statusCounts[s.value] || 0}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className="nf-loading">
                        <ProgressSpinner style={{ width: '46px', height: '46px' }} strokeWidth="4" />
                    </div>
                ) : (
                    <div className="nf-table-wrap">
                        <DataTable
                            value={filteredData}
                            filters={filters}
                            globalFilterFields={['notification_details', 'client_name', 'branch_name', 'created_by']}
                            paginator
                            rows={10}
                            rowsPerPageOptions={[10, 25, 50]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                            currentPageReportTemplate="{first} to {last} of {totalRecords} records"
                            tableStyle={{ minWidth: '65rem' }}
                            className="p-datatable-sm nf-table"
                            emptyMessage={`No ${statusFilter.toLowerCase()} notifications found`}
                            selectionMode="single"
                            selection={selectedNotification}
                            onSelectionChange={(e) => handleSelectionChange(e.value)}
                            dataKey="notification_id"
                        >
                            <Column field="notification_id" header="ID" sortable style={{ width: '70px' }} />
                            <Column field="notification_details" header="Notification" body={detailsBodyTemplate} sortable />
                            <Column field="client_name" header="Client" sortable style={{ width: '150px' }} />
                            <Column field="branch_name" header="Branch" body={branchBodyTemplate} sortable style={{ width: '150px' }} />
                            <Column field="expiry_date" header="Expiry" body={expiryBodyTemplate} sortable style={{ width: '175px' }} />
                            <Column field="created" header="Created" body={(row) => formatDisplayDate(row.created)} sortable style={{ width: '165px' }} />
                            <Column field="created_by" header="Created By" sortable style={{ width: '130px' }} />
                            <Column field="read_count" header="Read" body={readBodyTemplate} sortable style={{ width: '105px', textAlign: 'center' }} />
                            <Column field="status" header="Status" body={statusBodyTemplate} sortable style={{ width: '110px', textAlign: 'center' }} />
                            {superAdmin && <Column header="Actions" body={actionBodyTemplate} style={{ width: '85px', textAlign: 'center' }} />}
                        </DataTable>
                    </div>
                )}
            </div>

            {/* ── Read / unread breakdown for the selected row ────── */}
            <div className="nf-card">
                <div className="nf-card-head">
                    <div>
                        <h3 className="nf-card-title">
                            <i className="pi pi-eye" /> Read / Unread Status
                        </h3>
                        <p className="nf-card-sub" title={selectedNotification?.notification_details}>
                            {selectedNotification ? `#${selectedNotification.notification_id} — ${selectedNotification.notification_details}` : 'Select a notification above to see who has read it'}
                        </p>
                    </div>
                    {selectedNotification && (
                        <div className="flex align-items-center gap-2 flex-wrap">
                            <Pill tone="success">{readStatus.read_count || 0} Read</Pill>
                            <Pill tone="warning">{unreadCount} Unread</Pill>
                            <Pill tone="info">{readStatus.recipient_count || 0} Recipients</Pill>
                            <Button icon="pi pi-times" text rounded tooltip="Clear selection" tooltipOptions={{ position: 'top' }} onClick={() => handleSelectionChange(null)} />
                        </div>
                    )}
                </div>

                {!selectedNotification ? (
                    <div className="nf-empty">
                        <i className="pi pi-angle-double-up" />
                        Pick a notification from the list above
                    </div>
                ) : readStatusLoading ? (
                    <div className="nf-loading">
                        <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" />
                    </div>
                ) : (
                    <div className="nf-table-wrap">
                        <DataTable
                            value={readStatusRows}
                            paginator={readStatusRows.length > 8}
                            rows={8}
                            className="p-datatable-sm nf-table"
                            tableStyle={{ minWidth: '36rem' }}
                            emptyMessage="No users are targeted by this notification"
                            dataKey="user_id"
                        >
                            <Column field="username" header="User" sortable />
                            <Column field="role" header="Role" sortable style={{ width: '130px' }} />
                            <Column field="branch_name" header="Branch" body={(row) => row.branch_name || <Dash />} sortable style={{ width: '150px' }} />
                            <Column
                                field="is_read"
                                header="Status"
                                body={(row) => <Pill tone={row.is_read ? 'success' : 'warning'}>{row.is_read ? 'Read' : 'Unread'}</Pill>}
                                sortable
                                style={{ width: '110px', textAlign: 'center' }}
                            />
                            <Column field="read_at" header="Read At" body={(row) => (row.read_at ? formatDisplayDate(row.read_at) : <Dash />)} sortable style={{ width: '165px' }} />
                            <Column field="remarks" header="Remarks" body={remarksBodyTemplate} sortable />
                        </DataTable>
                    </div>
                )}
            </div>

            <NotificationsDialog
                visible={displayDialog}
                notificationData={editNotification}
                onHide={() => {
                    setDisplayDialog(false);
                    setEditNotification(null);
                }}
                onSave={handleSaveRequest}
            />
        </>
    );
};

export default NotificationsContent;
